import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import type { GPIOReadingUsedFor } from './../enums/GPIOReadingUsedFor';
import { formatDateIso } from '@/utilities/dateutils';

export class GPIOReading extends BaseObj {
    public CategoryIDs: number[];
    public __categoryids: number[];

    public EventID: number;
    public __eventid: number;

    public GroupIDs: number[];
    public __groupids: number[];

    public High: boolean;
    public __high: boolean;

    public readonly Id: string | null;

    public Port: number;
    public __port: number;

    public RaceContestantIDs: number[];
    public __racecontestantids: number[];

    public StationID: number;
    public __stationid: number;

    public Time: Date;
    public __time: Date;

    public TUID: string | null;
    public __tuid: string | null;

    public UsedFor: GPIOReadingUsedFor | null;
    public __usedfor: GPIOReadingUsedFor | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        CategoryIDs?: number[];
        EntityState?: EntityState;
        EventID?: number;
        GroupIDs?: number[];
        High?: boolean;
        Id?: string | null;
        Port?: number;
        RaceContestantIDs?: number[];
        StationID?: number;
        Time?: Date;
        TUID?: string | null;
        UsedFor?: GPIOReadingUsedFor | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<GPIOReading>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('GPIOReading tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.CategoryIDs = values.CategoryIDs ?? [];
        this.__categoryids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.CategoryIDs ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('CategoryIDs', oldVal, newVal),
        });
        Object.defineProperty(this, 'CategoryIDs', {
            get(): number[] {
                return this.__categoryids;
            },
            set(value: number[]) {
                this.__entityStateHandler.propertyChanged('CategoryIDs', this.__categoryids, value);
                this.__categoryids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: number[], newVal: number[]) =>
                        this.__entityStateHandler.propertyChanged('CategoryIDs', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GroupIDs = values.GroupIDs ?? [];
        this.__groupids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.GroupIDs ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('GroupIDs', oldVal, newVal),
        });
        Object.defineProperty(this, 'GroupIDs', {
            get(): number[] {
                return this.__groupids;
            },
            set(value: number[]) {
                this.__entityStateHandler.propertyChanged('GroupIDs', this.__groupids, value);
                this.__groupids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: number[], newVal: number[]) => this.__entityStateHandler.propertyChanged('GroupIDs', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.High = values.High ?? false;
        this.__high = values.High ?? false;
        Object.defineProperty(this, 'High', {
            get(): boolean {
                return this.__high;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('High', this.__high, value);
                this.__high = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Id = values.Id ?? '';

        this.Port = values.Port ?? 0;
        this.__port = values.Port ?? 0;
        Object.defineProperty(this, 'Port', {
            get(): number {
                return this.__port;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Port', this.__port, value);
                this.__port = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantIDs = values.RaceContestantIDs ?? [];
        this.__racecontestantids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RaceContestantIDs ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('RaceContestantIDs', oldVal, newVal),
        });
        Object.defineProperty(this, 'RaceContestantIDs', {
            get(): number[] {
                return this.__racecontestantids;
            },
            set(value: number[]) {
                this.__entityStateHandler.propertyChanged('RaceContestantIDs', this.__racecontestantids, value);
                this.__racecontestantids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: number[], newVal: number[]) =>
                        this.__entityStateHandler.propertyChanged('RaceContestantIDs', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.StationID = values.StationID ?? 0;
        this.__stationid = values.StationID ?? 0;
        Object.defineProperty(this, 'StationID', {
            get(): number {
                return this.__stationid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('StationID', this.__stationid, value);
                this.__stationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        this.__time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Time', {
            get(): Date {
                return this.__time;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Time', this.__time, value);
                this.__time = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TUID = values.TUID ?? null;
        this.__tuid = values.TUID ?? null;
        Object.defineProperty(this, 'TUID', {
            get(): string | null {
                return this.__tuid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('TUID', this.__tuid, value);
                this.__tuid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.UsedFor = values.UsedFor ?? 'None';
        this.__usedfor = values.UsedFor ?? 'None';
        Object.defineProperty(this, 'UsedFor', {
            get(): GPIOReadingUsedFor | null {
                return this.__usedfor;
            },
            set(value: GPIOReadingUsedFor | null) {
                this.__entityStateHandler.propertyChanged('UsedFor', this.__usedfor, value);
                this.__usedfor = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"CategoryIDs": [ ${this.CategoryIDs.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"GroupIDs": [ ${this.GroupIDs.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"High": ${JSON.stringify(this.High)}` +
            ',' +
            `"Id": ${JSON.stringify(this.Id)}` +
            ',' +
            `"Port": ${JSON.stringify(this.Port)}` +
            ',' +
            `"RaceContestantIDs": [ ${this.RaceContestantIDs.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"StationID": ${JSON.stringify(this.StationID)}` +
            ',' +
            `"Time": ${'"' + formatDateIso(this.Time) + '"'}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            ',' +
            `"UsedFor": ${this.UsedFor == null ? null : this.UsedFor == 'None' ? 0 : this.UsedFor == 'StartTime' ? 1 : this.UsedFor == 'GeneratedTime' ? 2 : this.UsedFor == 'GroupStartTime' ? 3 : this.UsedFor == 'CategoryStartTime' ? 4 : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of GPIOReading from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        CategoryIDs?: number[];
        EventID?: number;
        GroupIDs?: number[];
        High?: boolean;
        Port?: number;
        RaceContestantIDs?: number[];
        StationID?: number;
        Time?: Date;
        TUID?: string | null;
        UsedFor?: GPIOReadingUsedFor | null;
        EntityState?: 'Added' | 'Unchanged';
    }): GPIOReading {
        return new GPIOReading(values);
    }

    public static _FromServerJson(json: any): GPIOReading {
        return new GPIOReading({
            CategoryIDs: json.CategoryIDs,
            EventID: json.EventID,
            GroupIDs: json.GroupIDs,
            High: json.High,
            Id: json.Id ?? '',
            Port: json.Port,
            RaceContestantIDs: json.RaceContestantIDs,
            StationID: json.StationID,
            Time: json.Time instanceof Date ? json.Time : new Date(json.Time),
            TUID: json.TUID ?? '',
            UsedFor:
                json.UsedFor == null
                    ? null
                    : json.UsedFor == 0
                      ? 'None'
                      : json.UsedFor == 1
                        ? 'StartTime'
                        : json.UsedFor == 2
                          ? 'GeneratedTime'
                          : json.UsedFor == 3
                            ? 'GroupStartTime'
                            : json.UsedFor == 4
                              ? 'CategoryStartTime'
                              : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of GPIOReading from another instance of GPIOReading.
     * @param other The GPIOReading you want to clone
     * @returns A cloned GPIOReading
     */
    public static FromOther(other: GPIOReading): GPIOReading {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return GPIOReading._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return GPIOReading.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'CategoryIDs'
            | 'EventID'
            | 'GroupIDs'
            | 'High'
            | 'Id'
            | 'Port'
            | 'RaceContestantIDs'
            | 'StationID'
            | 'Time'
            | 'TUID'
            | 'UsedFor'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'GPIOReading';
}
